/**
 * Order "Explain" — a one-line plain-English narrative for a delivery leg,
 * from the Operations Agent (Zoe).
 *
 * The agent call is expensive (tool loop + LLM), so this is:
 *   - only ever triggered by an explicit button click, and
 *   - cached in localStorage per order reference with a TTL, so re-expanding a
 *     row (or reloading the wallboard) does not re-run it.
 *
 * A server-side shared cache would be better still; this keeps E3 frontend-only.
 */

import { submitInvestigation } from './investigatorApi';

export interface OrderNarrative {
  summary: string;
  detail?: string;
  computedAt: string;
}

const CACHE_PREFIX = 'order_explain_v1:';
const TTL_MS = 6 * 60 * 60 * 1000; // 6h — a stalled drop's story does not change minute to minute.

function cacheKey(ref: string): string {
  return `${CACHE_PREFIX}${ref}`;
}

export function readCachedNarrative(ref: string): OrderNarrative | null {
  try {
    const raw = localStorage.getItem(cacheKey(ref));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OrderNarrative;
    if (!parsed?.computedAt || Date.now() - Date.parse(parsed.computedAt) > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCachedNarrative(ref: string, value: OrderNarrative): void {
  try {
    localStorage.setItem(cacheKey(ref), JSON.stringify(value));
  } catch {
    // storage full / disabled — a cache miss next time is acceptable
  }
}

/**
 * Return the cached narrative if fresh; otherwise ask the agent, cache, return.
 * `force` skips the cache read (a "regenerate" affordance).
 */
export async function explainOrder(ref: string, force = false): Promise<OrderNarrative> {
  if (!force) {
    const cached = readCachedNarrative(ref);
    if (cached) return cached;
  }

  const res = await submitInvestigation({ question: `What happened to order ${ref}?` });
  const summary =
    res.presentation?.narrative?.answer?.trim() ||
    res.presentation?.narrative?.headline?.trim() ||
    res.summary?.trim() ||
    'No narrative was produced for this order.';
  const detail = res.detailed_explanation?.trim() || undefined;

  const narrative: OrderNarrative = { summary, detail, computedAt: new Date().toISOString() };
  writeCachedNarrative(ref, narrative);
  return narrative;
}
